import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { apiService } from '../../services/api'
import { ArrowLeft, Star, MessageSquare, Award, Plus, Eye } from 'lucide-react'
import { toast } from 'react-toastify'

interface VendorFeedback {
  id: string
  poId: string
  buyerId: string
  vendorId: string
  rating: number
  review?: string
  createdAt: string
  updatedAt: string
  po: {
    id: string
    poNumber: string
    totalAmount: number
    currency: string
    vendor: {
      companyName: string
      email: string
    }
    buyer: {
      companyName: string
    }
  }
  vendor: {
    companyName: string
    email: string
    firstName: string
    lastName: string
  }
  buyer: {
    companyName: string
    email: string
    firstName: string
    lastName: string
  }
}

const VendorFeedbackPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [feedbacks, setFeedbacks] = useState<VendorFeedback[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedFeedback, setSelectedFeedback] = useState<VendorFeedback | null>(null)
  const [isVendor, setIsVendor] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)

  useEffect(() => {
    if (!user) {
      navigate('/auth/login')
      return
    }
    // Check if user is a vendor
    const userIsVendor = user.role === 'VENDOR' || user.role === 'ENTERPRISE_VENDOR'
    setIsVendor(userIsVendor)
    loadFeedbacks(userIsVendor)
  }, [user, navigate])

  const loadFeedbacks = async (userIsVendor: boolean = false) => {
    try {
      setLoading(true)
      let response
      if (userIsVendor) {
        // For vendors: load feedback they've received
        response = await apiService.get(`/api/post-quote-activity/vendors/${user?.id}/feedback`)
        setFeedbacks((response as any).data?.feedback || [])
      } else {
        // For buyers: load feedback they've submitted
        response = await apiService.get('/api/post-quote-activity/feedback')
        setFeedbacks((response as any).data?.feedbacks || [])
      }
    } catch (error: any) {
      console.error('Error loading feedbacks:', error)
      toast.error(error.response?.data?.message || 'Failed to load feedbacks')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount)
  }

  const renderStars = (rating: number, size: 'sm' | 'md' | 'lg' = 'sm') => {
    const sizeClasses = {
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6'
    }

    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${sizeClasses[size]} ${
              star <= rating
                ? 'text-yellow-400 fill-current'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    )
  }

  const getAverageRating = () => {
    if (feedbacks.length === 0) return 0
    const total = feedbacks.reduce((sum, feedback) => sum + feedback.rating, 0)
    return (total / feedbacks.length).toFixed(1)
  }

  const getRatingDistribution = () => {
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
    feedbacks.forEach(feedback => {
      distribution[feedback.rating as keyof typeof distribution]++
    })
    return distribution
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading feedback...</p>
        </div>
      </div>
    )
  }

  const averageRating = getAverageRating()
  const ratingDistribution = getRatingDistribution()

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/enterprise/post-quote-activity')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {isVendor ? 'My Customer Reviews' : 'Vendor Feedback & Ratings'}
                </h1>
                <p className="text-gray-600 mt-1">
                  {isVendor 
                    ? 'Reviews and ratings from your customers'
                    : 'Manage vendor reviews and ratings'
                  }
                </p>
              </div>
            </div>
            {!isVendor && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Submit Feedback</span>
              </button>
            )}
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Average Rating</h3>
              <Award className="w-6 h-6 text-yellow-500" />
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-3xl font-bold text-gray-900">{averageRating}</span>
              {renderStars(Math.round(parseFloat(averageRating.toString())), 'md')}
            </div>
            <p className="text-sm text-gray-600 mt-2">Based on {feedbacks.length} reviews</p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Total Reviews</h3>
              <MessageSquare className="w-6 h-6 text-blue-500" />
            </div>
            <span className="text-3xl font-bold text-gray-900">{feedbacks.length}</span>
            <p className="text-sm text-gray-600 mt-2">Vendor feedback submissions</p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Rating Distribution</h3>
              <Star className="w-6 h-6 text-yellow-500" />
            </div>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((rating) => (
                <div key={rating} className="flex items-center space-x-2">
                  <span className="text-sm w-6">{rating}</span>
                  <Star className="w-4 h-4 text-yellow-400 fill-current" />
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-yellow-400 h-2 rounded-full"
                      style={{
                        width: feedbacks.length > 0 
                          ? `${(ratingDistribution[rating as keyof typeof ratingDistribution] / feedbacks.length) * 100}%`
                          : '0%'
                      }}
                    ></div>
                  </div>
                  <span className="text-sm text-gray-600 w-8">
                    {ratingDistribution[rating as keyof typeof ratingDistribution]}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Feedback List */}
        {feedbacks.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {isVendor ? 'No Reviews Yet' : 'No Feedback Yet'}
            </h3>
            <p className="text-gray-600 mb-6">
              {isVendor 
                ? 'You haven\'t received any customer reviews yet. Complete some orders to start building your reputation.'
                : 'Start building vendor relationships by submitting your first feedback.'
              }
            </p>
            {!isVendor && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg"
              >
                Submit First Feedback
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-6">
            {feedbacks.map((feedback) => (
              <div key={feedback.id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {isVendor ? feedback.po.buyer.companyName : feedback.po.vendor.companyName}
                      </h3>
                      {renderStars(feedback.rating, 'md')}
                      <span className="text-sm text-gray-600">({feedback.rating}/5)</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      {isVendor ? 'Customer: ' : 'Vendor: '}
                      {isVendor ? feedback.po.buyer.companyName : feedback.po.vendor.companyName} •
                      PO: {feedback.po.poNumber} •
                      {formatCurrency(feedback.po.totalAmount, feedback.po.currency)}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setSelectedFeedback(feedback)
                        setShowDetailModal(true)
                      }}
                      className="text-gray-600 hover:text-gray-700 p-1"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {feedback.review && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <p className="text-gray-700">{feedback.review}</p>
                  </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-500">
                    Submitted on {formatDate(feedback.createdAt)}
                  </p>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">
                      By {feedback.po.buyer.companyName}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Feedback Modal */}
      {showCreateModal && (
        <CreateFeedbackModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false)
            loadFeedbacks()
          }}
        />
      )}

      {/* Feedback Detail Modal */}
      {showDetailModal && selectedFeedback && (
        <FeedbackDetailModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false)
            setSelectedFeedback(null)
          }}
          feedback={selectedFeedback}
        />
      )}
    </div>
  )
}

// Create Feedback Modal Component
const CreateFeedbackModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    poId: '',
    rating: 5,
    review: ''
  })
  const [loading, setLoading] = useState(false)
  const [purchaseOrders, setPurchaseOrders] = useState<any[]>([])

  useEffect(() => {
    if (isOpen) {
      loadPurchaseOrders()
    }
  }, [isOpen])

  const loadPurchaseOrders = async () => {
    try {
      const response = await apiService.get('/api/post-quote-activity/purchase-orders')
      console.log('🔍 Raw purchase orders response:', response)
      
      // First get all ACCEPTED purchase orders
      const allAcceptedPOs = (response as any).data?.filter((po: any) => po.status === 'ACCEPTED') || []
      console.log('📋 All accepted POs:', allAcceptedPOs.length, allAcceptedPOs)
      
      // Then filter for delivered orders OR orders with paid invoices
      // TEMPORARILY ALLOWING DUPLICATE FEEDBACK FOR TESTING
      const eligiblePOs = allAcceptedPOs.filter((po: any) => {
        const isDelivered = po.fulfillments?.some((f: any) => f.status === 'DELIVERED')
        const hasPaidInvoice = po.invoices?.some((invoice: any) => invoice.status === 'PAID')
        const hasNoFeedback = !po.feedback || po.feedback.length === 0
        
        // TESTING: Allow feedback even if already exists
        const isEligible = (isDelivered || hasPaidInvoice) // Removed && hasNoFeedback
        console.log(`📝 PO ${po.poNumber}: delivered=${isDelivered}, paidInvoice=${hasPaidInvoice}, noFeedback=${hasNoFeedback}, eligible=${isEligible}`)
        console.log(`🔍 PO ${po.poNumber} feedback:`, po.feedback)
        
        return isEligible
      })
      
      console.log('✅ Eligible POs for feedback:', eligiblePOs.length, eligiblePOs)
      setPurchaseOrders(eligiblePOs)
    } catch (error) {
      console.error('❌ Error loading purchase orders:', error)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.poId || !formData.rating) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      setLoading(true)
      await apiService.post('/api/post-quote-activity/feedback', formData)
      toast.success('Feedback submitted successfully')
      onSuccess()
    } catch (error: any) {
      console.error('Error submitting feedback:', error)
      toast.error(error.response?.data?.message || 'Failed to submit feedback')
    } finally {
      setLoading(false)
    }
  }

  const renderStarRating = () => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => setFormData({ ...formData, rating: star })}
            className={`w-8 h-8 ${
              star <= formData.rating
                ? 'text-yellow-400 fill-current'
                : 'text-gray-300 hover:text-yellow-300'
            } transition-colors`}
          >
            <Star className="w-full h-full" />
          </button>
        ))}
      </div>
    )
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4">Submit Vendor Feedback</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Purchase Order *
            </label>
            <select
              value={formData.poId}
              onChange={(e) => setFormData({ ...formData, poId: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            >
              <option value="">Select Purchase Order</option>
              {purchaseOrders.map((po) => (
                <option key={po.id} value={po.id}>
                  {po.poNumber} - {po.vendor.companyName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rating *
            </label>
            <div className="flex items-center space-x-3">
              {renderStarRating()}
              <span className="text-sm text-gray-600">({formData.rating}/5)</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Review
            </label>
            <textarea
              value={formData.review}
              onChange={(e) => setFormData({ ...formData, review: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={4}
              placeholder="Share your experience with this vendor..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Feedback Detail Modal Component
const FeedbackDetailModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  feedback: VendorFeedback
}> = ({ isOpen, onClose, feedback }) => {
  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${
              star <= rating
                ? 'text-yellow-400 fill-current'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    )
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-6">
          <h2 className="text-xl font-semibold">Feedback Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>

        <div className="space-y-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900">
                {feedback.po.vendor.companyName}
              </h3>
              <div className="flex items-center space-x-2">
                {renderStars(feedback.rating)}
                <span className="text-sm text-gray-600">({feedback.rating}/5)</span>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Purchase Order: {feedback.po.poNumber}
            </p>
            <p className="text-sm text-gray-600">
              Order Value: {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: feedback.po.currency
              }).format(feedback.po.totalAmount)}
            </p>
          </div>

          {feedback.review && (
            <div>
              <h4 className="font-semibold mb-2">Review</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700">{feedback.review}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Submitted By</p>
              <p className="font-semibold">{feedback.po.buyer.companyName}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Submitted On</p>
              <p className="font-semibold">{new Date(feedback.createdAt).toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default VendorFeedbackPage